import React, { useEffect, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import StatTile from '../../components/stats/StatTile';
import RankBars from '../../components/stats/RankBars';
import { CHART, formatCompact, formatMeasure, formatNumber, formatPeriod } from '../../utils/statsFormat';

const MEASURES = [
  { key: 'quantite', label: 'Quantité', unit: 'unités' },
  { key: 'valeurHT', label: 'Valeur HT', unit: 'DA', money: true },
  { key: 'valeurTTC', label: 'Valeur TTC', unit: 'DA', money: true },
  { key: 'demandes', label: 'Demandes', unit: '' },
];

const RANKINGS = [
  { dim: 'produit', title: 'Produits' },
  { dim: 'distributeur', title: 'Distributeurs' },
  { dim: 'zone', title: 'Zones' },
  { dim: 'region', title: 'Régions' },
  { dim: 'animateur', title: 'Animateurs' },
  { dim: 'motif', title: 'Motifs' },
  { dim: 'categorie', title: 'Catégories' },
  { dim: 'type', title: 'Types de produit' },
];

// Ordre du parcours d'une demande, pour lire les étapes dans le bon sens.
const STAGE_ORDER = [
  'marketing', 'marketing_approbation', 'force_de_vente', 'chef_de_zone',
  'distributeur', 'marketing_review', 'directrice', 'commercial',
];
const byStage = (a, b) => STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage);

const STATUS_COLOR = { 'Terminée': CHART.good, 'En cours': CHART.series, 'Refusée': CHART.critical, 'Annulée': CHART.inkSecondary };
const AGES = [
  { key: 'moins3', label: 'Moins de 3 jours' },
  { key: 'de3a7', label: '3 à 7 jours' },
  { key: 'de7a15', label: '7 à 15 jours' },
  { key: 'plus15', label: 'Plus de 15 jours' },
];

const Card = ({ title, subtitle, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white p-4">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const Empty = () => <p className="py-6 text-center text-sm text-gray-400">Aucune donnée sur cette période</p>;

const SerieTooltip = ({ active, payload, measure }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow">
      <p className="font-medium text-gray-900">{formatPeriod(point.periode)}</p>
      <p className="text-gray-700">{measure.label} : <span className="font-semibold tabular-nums">{formatMeasure(point[measure.key], measure.unit)}</span></p>
      <p className="text-gray-500">{formatNumber(point.demandes)} demande{point.demandes > 1 ? 's' : ''}</p>
    </div>
  );
};

const StatsOverview = ({ query, onFilter }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [measureKey, setMeasureKey] = useState('quantite');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.statistics
      .overview(query)
      .then((res) => !cancelled && setData(res.data.data))
      .catch((err) => !cancelled && setError(err.response?.data?.error || 'Impossible de charger les statistiques'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [query]);

  if (error) return <p className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</p>;
  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary-600" />
      </div>
    );
  }

  const measures = MEASURES.filter((m) => data.values || !m.money);
  const measure = measures.find((m) => m.key === measureKey) || measures[0];
  const { kpis, evolution: evo } = data;
  const e = (key) => (evo ? evo[key] : null);
  const etapes = [...data.etapes].sort(byStage);
  const attente = [...data.attente].sort(byStage);
  const maxDelay = Math.max(...etapes.map((s) => s.delaiMoyenJours || 0), 0);
  const maxWaiting = Math.max(...attente.map((s) => s.total), 0);
  const totalStatut = data.parStatut.reduce((sum, s) => sum + s.demandes, 0);

  return (
    <div className={`space-y-6 ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
      {/* Indicateurs de tête */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Demandes" value={formatNumber(kpis.demandes)} evolution={e('demandes')}
          hint={`${formatNumber(data.aTraiter)} à traiter par vous`} />
        <StatTile label="Terminées" value={formatNumber(kpis.terminees)} evolution={e('terminees')}
          hint={`${formatNumber(kpis.enCours)} en cours`} />
        <StatTile label="Taux de refus" value={formatMeasure(kpis.tauxRefus, '%')} evolution={e('tauxRefus')} lowerIsBetter
          hint={`${formatNumber(kpis.refusees)} refusée${kpis.refusees > 1 ? 's' : ''}`} />
        <StatTile label="Délai moyen de traitement" value={formatMeasure(kpis.delaiMoyenJours, 'jours')}
          evolution={e('delaiMoyenJours')} lowerIsBetter hint="De la création à la clôture" />
        <StatTile label="Quantité attribuée" value={formatNumber(kpis.quantite)} evolution={e('quantite')} hint="Hors demandes refusées" />
        <StatTile label="Distributeurs actifs" value={formatNumber(kpis.distributeursActifs)} evolution={e('distributeursActifs')}
          hint="Sur les demandes actives" />
        {data.values && (
          <>
            <StatTile label="Valeur HT" value={formatMeasure(kpis.valeurHT, 'DA')} evolution={e('valeurHT')} hint="Au prix appliqué" />
            <StatTile label="Valeur TTC" value={formatMeasure(kpis.valeurTTC, 'DA')} evolution={e('valeurTTC')}
              hint={`dont ${formatMeasure(kpis.tva, 'DA')} de TVA`} />
            <StatTile label="Panier moyen HT" value={formatMeasure(kpis.panierMoyen, 'DA')} evolution={e('panierMoyen')}
              hint="Par demande active" />
            <StatTile label="Remises accordées" value={formatMeasure(kpis.remiseHT, 'DA')} evolution={e('remiseHT')}
              hint={`${formatMeasure(kpis.remisePct, '%')} du tarif catalogue`} />
          </>
        )}
      </div>

      {data.values && kpis.partEstimeePct > 0 && (
        <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span>
            <strong>{formatMeasure(kpis.partEstimeePct, '%')} de la valeur est estimée.</strong> Ces demandes n'ont pas encore de prix
            figé par le commercial : elles sont valorisées au tarif catalogue du jour.
          </span>
        </p>
      )}

      {/* Mesure affichée dans la courbe et les classements */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600">Mesure affichée :</span>
        <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" role="group" aria-label="Mesure affichée">
          {measures.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMeasureKey(m.key)}
              aria-pressed={measure.key === m.key}
              className={`rounded px-3 py-1 text-sm ${measure.key === m.key ? 'bg-primary-600 font-medium text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <Card title={`Évolution — ${measure.label}${measure.unit ? ` (${measure.unit})` : ''}`} subtitle="Hors demandes refusées ou annulées">
        {data.serie.length === 0 ? <Empty /> : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.serie} margin={{ top: 8, right: 40, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="periode" tickFormatter={formatPeriod} tick={{ fontSize: 12, fill: CHART.inkSecondary }}
                  tickLine={false} axisLine={{ stroke: CHART.grid }} minTickGap={16} interval="preserveStartEnd" />
                <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12, fill: CHART.inkSecondary }}
                  tickLine={false} axisLine={false} width={56} />
                <Tooltip content={<SerieTooltip measure={measure} />} cursor={{ stroke: CHART.inkSecondary, strokeDasharray: '3 3' }} />
                <Area type="monotone" dataKey={measure.key} stroke={CHART.series} strokeWidth={2}
                  fill={CHART.seriesSoft} fillOpacity={0.6} dot={data.serie.length <= 31 ? { r: 3, fill: CHART.series } : false}
                  activeDot={{ r: 5 }} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {RANKINGS.map(({ dim, title }) => (
          <RankBars key={dim} title={title} rows={data.tops[dim] || []} measure={measure}
            onSelect={(value) => onFilter(dim, value)} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Délai et refus par étape" subtitle="Temps passé dans l'étape, de l'arrivée de la demande à sa validation">
          {etapes.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-2 pr-3 font-medium">Étape</th>
                    <th className="px-3 py-2 text-right font-medium">Traitées</th>
                    <th className="px-3 py-2 font-medium">Délai moyen</th>
                    <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Médiane</th>
                    <th className="whitespace-nowrap py-2 pl-3 text-right font-medium">Taux de refus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {etapes.map((s) => (
                    <tr key={s.stage}>
                      <td className="py-2 pr-3 text-gray-900">{s.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{formatNumber(s.traitees)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 flex-shrink-0 rounded-full" style={{ backgroundColor: CHART.track }}>
                            <div className="h-2 rounded-full" style={{ width: `${maxDelay ? (s.delaiMoyenJours / maxDelay) * 100 : 0}%`, backgroundColor: CHART.series }} />
                          </div>
                          <span className="whitespace-nowrap tabular-nums text-gray-900">{formatMeasure(s.delaiMoyenJours, 'jours')}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{formatMeasure(s.delaiMedianJours, 'jours')}</td>
                      <td className="py-2 pl-3 text-right tabular-nums text-gray-700">{formatMeasure(s.tauxRefus, '%')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Demandes en attente par étape" subtitle="Réparties selon leur ancienneté dans l'étape">
          {attente.length === 0 ? <p className="py-6 text-center text-sm text-gray-400">Aucune demande en attente</p> : (
            <>
              <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                {AGES.map((a, i) => (
                  <li key={a.key} className="flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CHART.age[i] }} />
                    {a.label}
                  </li>
                ))}
              </ul>
              <ul className="space-y-3">
                {attente.map((s) => (
                  <li key={s.stage}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-gray-900">{s.label}</span>
                      <span className="text-gray-600">
                        <span className="font-medium tabular-nums text-gray-900">{formatNumber(s.total)}</span>
                        {' '}· la plus ancienne : {formatMeasure(s.plusAncienneJours, 'jours')}
                      </span>
                    </div>
                    <div className="mt-1 flex h-3 gap-0.5" style={{ width: `${maxWaiting ? Math.max(6, (s.total / maxWaiting) * 100) : 0}%` }}>
                      {AGES.map((a, i) => s[a.key] > 0 && (
                        <div key={a.key} className="h-3 rounded-sm" title={`${a.label} : ${s[a.key]}`}
                          style={{ flexGrow: s[a.key], flexBasis: 0, backgroundColor: CHART.age[i] }} />
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      <Card title="Répartition par statut">
        {totalStatut === 0 ? <Empty /> : (
          <>
            <div className="flex h-3 gap-0.5">
              {data.parStatut.map((s) => (
                <div key={s.label} className="h-3 rounded-sm" title={`${s.label} : ${s.demandes}`}
                  style={{ flexGrow: s.demandes, flexBasis: 0, backgroundColor: STATUS_COLOR[s.label] || CHART.inkSecondary }} />
              ))}
            </div>
            <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              {data.parStatut.map((s) => (
                <li key={s.label} className="flex items-center gap-2 text-gray-700">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: STATUS_COLOR[s.label] || CHART.inkSecondary }} />
                  {s.label} : <span className="font-medium tabular-nums text-gray-900">{formatNumber(s.demandes)}</span>
                  <span className="text-gray-500">({formatMeasure((s.demandes / totalStatut) * 100, '%')})</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
};

export default StatsOverview;
